import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCheck, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PunchCard } from '@/components/staffAttendance/PunchCard';
import { RejectDialog } from '@/components/staffAttendance/RejectDialog';
import { CollarSelect, SectionSelect, WorkerTypeSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';

export const ApprovalQueue = ({ sections, onChanged, search = '' }) => {
  const [date, setDate] = useState(todayIST());
  const [section, setSection] = useState('');
  const [wt, setWt] = useState('');
  const [collar, setCollar] = useState('');
  const [split, setSplit] = useState(null);
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setRows(null);
    staffAttApi.queue({ date: date || undefined, section: section || undefined, status, worker_type: wt || undefined, collar: collar || undefined })
      .then((d) => { if (alive) { setRows(d.punches); setSplit(d.collar_split); } })
      .catch((e) => { if (alive) { setRows([]); toast.error(errMsg(e, 'Failed to load approvals')); } });
    return () => { alive = false; };
  }, [date, section, status, wt, collar]);
  useEffect(() => load(), [load]);

  const pend = useMemo(() => (rows || []).filter((p) => p.status === 'pending'), [rows]);
  const shown = useMemo(() => (rows || []).filter((p) => matchStaff(search, p.staff_name, p.staff_id)), [rows, search]);
  const matched = pend.filter((p) => p.face_result === 'match' && p.on_site !== false).length;
  const offsite = pend.filter((p) => p.on_site === false).length;
  const refresh = () => { load(); onChanged?.(); };

  const approve = async (p) => { try { await staffAttApi.approve(p.punch_id); toast.success(`Approved ${p.staff_name}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const reject = async (p, reason) => { try { await staffAttApi.reject(p.punch_id, reason); toast.success(`Rejected ${p.staff_name}`); setRejectFor(null); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const useAsRef = async (p) => {
    if (!window.confirm(`Use this selfie as ${p.staff_name}'s reference photo?\n\nConfirm the staff member was told the photo is used only to verify attendance.`)) return;
    try { await staffAttApi.useAsReference(p.punch_id); toast.success('Reference photo saved'); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approveAll = async () => {
    setBulkBusy(true);
    try { const r = await staffAttApi.approveMatched({ date: date || undefined, section: section || undefined, worker_type: wt || undefined, collar: collar || undefined }); toast.success(`Approved ${r.approved} matched punch(es)`); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="approval-queue">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[160px] bg-white" data-testid="queue-date-input" />
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="queue-section-select" />
        <WorkerTypeSelect value={wt} onChange={setWt} testId="queue-worker-select" />
        <CollarSelect value={collar} onChange={setCollar} testId="queue-collar-select" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px] bg-white" data-testid="queue-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['pending', 'approved', 'rejected', 'all'].map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={refresh} data-testid="queue-refresh"><RefreshCw className="w-4 h-4" /></Button>
        {status === 'pending' && (
          <Button onClick={approveAll} disabled={!matched || bulkBusy} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="queue-approve-matched">
            {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1" />}Approve all matched ({matched})
          </Button>
        )}
      </div>
      <CollarTiles split={split} value={collar} onPick={setCollar} metric="Punches" testId="queue-collar-tiles" />
      {status === 'pending' && offsite > 0 && (
        <p className="text-sm text-slate-700 bg-slate-100 rounded-lg px-3 py-2" data-testid="queue-offsite-note">
          {offsite} off-site punch(es) are not included in “Approve all matched” — approve or reject each one.
        </p>
      )}
      {rows === null && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
      {rows && rows.length === 0 && <p className="text-center text-gray-400 py-10" data-testid="queue-empty">Nothing here for this filter.</p>}
      {rows && rows.length > 0 && <SearchNote q={search} shown={shown.length} total={rows.length} testId="queue-search-note" />}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((p) => <PunchCard key={p.punch_id} p={p} onApprove={approve} onReject={setRejectFor} onUseAsRef={useAsRef} />)}
      </div>
      <RejectDialog punch={rejectFor} onClose={() => setRejectFor(null)} onConfirm={reject} />
    </div>
  );
};

export default ApprovalQueue;
